import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  AiScribeContentOutputType,
  AiScribeJob,
  NewAiScribeCreated,
  SourceAiScribeJob,
} from '../interfaces/aiScribeJob.interface';
import { map, Observable, switchMap } from 'rxjs';

export interface FileAssetsResponse {
  id: string;
  bucketName: string;
  objectKey: string;
  fileExtension: string | null;
  durationSeconds: number | null;
  uploadedBy: string | null;
  createdAt: Date | null;
}

export interface AiScribeResponse {
  sessionId: string;
  recordingFileId: string | null;
  jobName: string | null;
  speciality: string | null;
  scribeType: string | null;
  languageCode: string | null;
  approvedStatus: string | null;
  isApproved: boolean | null;
  status: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  aiScribeId: string;
}

export interface CreateAiScribeLogPayload {
  aiScribeId: string;
  status?: string;
  eventType?: string;
  comments?: string;
}

export interface SubmittedReportPick {
  formType: string;
  generationProfileTargetId: string | null;
}

export interface UpdateAiScribePayload {
  recordingFileId?: string;
  status?: string;
  approvedStatus?: string;
  isApproved?: boolean;
  jobName?: string;
  speciality?: string;
  scribeType?: string;
  languageCode?: string;
  formType?: string;
}

export class UpdateAiScribeContentDto {
  inputText?: string;
  generatedOutput?: {
    output: string;
  };
}

export interface CreateAiScribeTrackingDto {
  sessionId: string;
  aiScribeId: string;
  editedBy: string; // The ID of the user who made the edit
  oldOutput?: any;
  newOutput?: any;
  comments?: string;
}

export interface AiScribeContentResponse {
  inputText: string | null;
  generatedOutput: {
    output: string;
  } | null;
}

export interface AiScribeContentSignedUrlResponse {
  aiScribeId: string;
  sessionId: string;
  /** Row the URLs belong to — echoed back so a caller can confirm what it got. */
  contentId?: string;
  outputType?: AiScribeContentOutputType | null;
  version?: number | null;
  /**
   * The output object's S3 key. Its EXTENSION is the only reliable signal of
   * the payload's shape, which is not uniform across row types: a transcript /
   * summary row is raw text (`.md` or `.html`), a PR or RFA row is the JSON the
   * Temporal worker wrote. Parse accordingly — see fetchScribeOutputFromS3.
   */
  outputS3Key?: string | null;
  inputSignedUrl: string | null;
  outputSignedUrl: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AiScribeService {
  constructor(private readonly httpService: HttpClient) {}

  getAiScribesForSession(
    session_id: string,
    includes?: string,
  ): Observable<SourceAiScribeJob[]> {
    return this.httpService.get<SourceAiScribeJob[]>(
      environment.api_host +
        `sessions/${session_id}/ai-scribe?includes=${includes || ''}`,
    );
  }

  createFileAsset(
    sessionId: string,
    bucketName: string,
    object_key: string,
    file_extension: string,
    uploaded_by: string,
    duration_seconds: number,
    // Where each take starts, for a recording of more than one: the server
    // joins them into one file for playback.
    segments?: { byteOffset: number; startSeconds: number }[],
  ): Observable<FileAssetsResponse> {
    return this.httpService.post<FileAssetsResponse>(
      environment.api_host + `sessions/${sessionId}/file-assets/create`,
      {
        bucketName: bucketName,
        objectKey: object_key,
        fileExtension: file_extension,
        uploadedBy: uploaded_by,
        durationSeconds: duration_seconds,
        ...(segments ? { metadata: { segments } } : {}),
      },
    );
  }

  /**
   * `generationProfileTargetId` is the DOCX template package picked in the
   * creation form. Sent as `selection` because the API stores it as a
   * generation_selections row against the new scribe — the same shape
   * POST /sessions already uses for a case-level pick. Omitted when the user
   * made no choice, in which case the start path resolves the org default.
   */
  createAiScribe(
    sessionId: string,
    jobName: string,
    speciality: string,
    scribeType: string,
    languageCode: string,
    formType: string,
    generationProfileTargetId?: string | null,
  ): Observable<AiScribeResponse> {
    return this.httpService.post<AiScribeResponse>(
      environment.api_host + `sessions/${sessionId}/ai-scribe`,
      {
        jobName,
        speciality,
        scribeType,
        languageCode,
        formType,
        ...(generationProfileTargetId
          ? { selection: { id: generationProfileTargetId } }
          : {}),
      },
    );
  }

  createAiScribeContents(
    sessionId: string,
    aiScribeId: string,
  ): Observable<any> {
    return this.httpService.post<any>(
      environment.api_host +
        `sessions/${sessionId}/ai-scribe/${aiScribeId}/content`,
      {
        inputText: '',
        generatedOutput: {},
      },
    );
  }

  getAiScribeContent(
    sessionId: string,
    aiScribeId: string,
  ): Observable<AiScribeContentResponse> {
    return this.httpService.get<AiScribeContentResponse>(
      environment.api_host +
        `sessions/${sessionId}/ai-scribe/${aiScribeId}/content`,
    );
  }

  /**
   * Signed URLs for one content row.
   *
   * With no `options` the API resolves the scribe's TRANSCRIPT/LEGACY row —
   * the original behaviour, which the transcript fetch depends on. Pass
   * `outputType` for the newest row of a type, or `contentId` for an exact
   * row (the only way to name a superseded version once a report has been
   * regenerated).
   */
  getAiScribeContentSignedURL(
    sessionId: string,
    aiScribeId: string,
    options?: { outputType?: AiScribeContentOutputType; contentId?: string },
  ): Observable<AiScribeContentSignedUrlResponse> {
    let params = new HttpParams();
    if (options?.contentId) {
      params = params.set('contentId', options.contentId);
    } else if (options?.outputType) {
      params = params.set('outputType', options.outputType);
    }
    return this.httpService.get<AiScribeContentSignedUrlResponse>(
      environment.api_host +
        `sessions/${sessionId}/ai-scribe/${aiScribeId}/content/signed-url`,
      { params },
    );
  }

  updateAiScribe(
    sessionId: string,
    aiScribeId: string,
    payload: UpdateAiScribePayload,
  ): Observable<AiScribeJob> {
    const url =
      environment.api_host + `sessions/${sessionId}/ai-scribe/${aiScribeId}`;
    return this.httpService.patch<AiScribeJob>(url, payload);
  }

  updateAiScribeContent(
    sessionId: string,
    aiScribeId: string,
    payload: UpdateAiScribeContentDto,
  ): Observable<AiScribeJob> {
    const url =
      environment.api_host +
      `sessions/${sessionId}/ai-scribe/update-scribe-content/${sessionId}/${aiScribeId}`;
    return this.httpService.patch<AiScribeJob>(url, payload);
  }

  createAiScribeTracking(payload: CreateAiScribeTrackingDto): Observable<any> {
    const url = environment.api_host + 'tracking/ai-scribe/create';
    return this.httpService.post<any>(url, payload);
  }

  createAiScribeLog(
    sessionId: string,
    payload: CreateAiScribeLogPayload,
  ): Observable<any> {
    const createLog$ = this.httpService.post<any>(
      environment.api_host + `sessions/${sessionId}/ai-scribe/create-log`,
      payload,
    );

    if (!payload.status) {
      return createLog$;
    }

    return createLog$.pipe(
      switchMap((logResponse) =>
        this.updateAiScribe(sessionId, payload.aiScribeId, {
          status: payload.status,
        }).pipe(map(() => logResponse)),
      ),
    );
  }

  triggerSessionProcessing(
    connection_id: string,
    sessionId: string,
    jobId: string,
    audiourl: string[],
    language_code: string,
    user_id: string,
    org_id: string,
    speciality?: string,
    retry_count?: number,
  ): Observable<any> {
    return this.httpService.post<any>(
      environment.api_host + `sessions/${sessionId}/ai-scribe/trigger-whisper`,
      {
        connection_id: connection_id,
        sessionId: sessionId,
        jobId: jobId,
        audiourl: audiourl,
        language_code: language_code,
        speciality: speciality,
        user_id: user_id,
        org_id: org_id,
        retry_count,
      },
    );
  }

  triggerSessionProcessingAiGateway(
    sessionId: string,
    jobId: string,
    audioS3Keys: string[],
    user_id: string,
    org_id: string,
    retry_count?: number,
    reportPick?: SubmittedReportPick,
  ): Observable<any> {
    const validAudioS3Keys = audioS3Keys.filter(
      (key) => typeof key === 'string' && key.trim().length > 0,
    );

    if (validAudioS3Keys.length === 0) {
      throw new Error(
        'Cannot trigger AI gateway processing without at least one audio S3 key.',
      );
    }

    return this.httpService.post<any>(
      environment.api_host +
        `sessions/${sessionId}/ai-scribe/trigger-ai-gateway`,
      {
        sessionId: sessionId,
        jobId: jobId,
        audios3keys: validAudioS3Keys,
        user_id: user_id,
        org_id: org_id,
        retry_count,
        ...this.reportPickBody(reportPick),
      },
    );
  }

  CreateAiScribeDictation(
    audioText: string,
    title: string,
    speciality: string,
    sessionId: string,
    scribeType: string,
    formType: string,
    generationProfileTargetId?: string | null,
  ): Observable<NewAiScribeCreated> {
    return this.httpService.post<NewAiScribeCreated>(
      environment.api_host + `sessions/${sessionId}/ai-scribe/dictation`,
      {
        speciality: speciality,
        inputText: audioText,
        jobName: title,
        scribeType: scribeType,
        formType: formType,
        ...(generationProfileTargetId
          ? { selection: { id: generationProfileTargetId } }
          : {}),
      },
    );
  }

  saveAiScribeTemplates(
    sessionId: string,
    aiScribeId: string,
    selectedTemplateIds: string[],
  ): Observable<NewAiScribeCreated> {
    return this.httpService.post<NewAiScribeCreated>(
      environment.api_host + `sessions/${sessionId}/ai-scribe/templates`,
      {
        aiScribeId: aiScribeId,
        templateIds: selectedTemplateIds,
      },
    );
  }

  triggerDictationProcessing(
    connection_id: string,
    sessionId: string,
    jobId: string,
    speciality: string,
    user_id: string,
    org_id: string,
    retry_count?: number,
    reportPick?: SubmittedReportPick,
  ) {
    return this.httpService.post<NewAiScribeCreated>(
      environment.api_host + `sessions/${sessionId}/ai-scribe/trigger-scribe`,
      {
        connection_id,
        sessionId,
        jobId,
        speciality,
        user_id,
        org_id,
        retry_count,
        ...this.reportPickBody(reportPick),
      },
    );
  }

  private reportPickBody(reportPick?: SubmittedReportPick) {
    if (!reportPick) return {};
    return {
      formType: reportPick.formType,
      selection: reportPick.generationProfileTargetId
        ? { id: reportPick.generationProfileTargetId }
        : null,
    };
  }

  getFileAsset(
    sessionId: string,
    fileAssetId: string,
  ): Observable<FileAssetsResponse> {
    return this.httpService.get<FileAssetsResponse>(
      environment.api_host +
        `sessions/${sessionId}/file-assets/find-one/${fileAssetId}`,
    );
  }

  // Latest AUDIO_CONVERSION status per aiScribe for a session, so the media
  // library can distinguish a running conversion ('CONVERTING') from a failed one.
  getAudioConversionStatuses(sessionId: string): Observable<{
    statuses: {
      aiScribeId: string;
      status: 'CONVERTING' | 'FAILED' | 'COMPLETED';
    }[];
  }> {
    return this.httpService.get<{
      statuses: {
        aiScribeId: string;
        status: 'CONVERTING' | 'FAILED' | 'COMPLETED';
      }[];
    }>(
      environment.api_host +
        `sessions/${sessionId}/ai-scribe/audio-conversion-status`,
    );
  }

  updateAiScribeStatus(
    sessionId: string,
    aiScribeId: string,
    status: string,
  ): Observable<AiScribeJob> {
    const url =
      environment.api_host + `sessions/${sessionId}/ai-scribe/${aiScribeId}`;
    const payload = { status };
    console.log('Updating AI Scribe status with payload:', payload);
    return this.httpService.patch<AiScribeJob>(url, payload);
  }

  /**
   * Permanently delete an AI Scribe (recording + transcription).
   *
   * Returns 409 with a user-facing `message` when it's no longer deletable, so
   * show the server's message on error — our local check can be a tick stale.
   */
  deleteAiScribe(
    sessionId: string,
    aiScribeId: string,
  ): Observable<{ aiScribeId: string; deleted: boolean }> {
    return this.httpService.delete<{ aiScribeId: string; deleted: boolean }>(
      environment.api_host + `sessions/${sessionId}/ai-scribe/${aiScribeId}`,
    );
  }

  clearAiScribeOutput(sessionId: string, processId: string): Observable<any> {
    const url = `${environment.api_host}sessions/${sessionId}/ai-scribe/update-scribe-content/${sessionId}/${processId}`;

    const payload = {
      generatedOutput: {
        output: null,
      },
    };

    console.log('Clearing output with payload:', payload);

    return this.httpService.patch(url, payload);
  }
}
